import { Dashboard } from '../../models/Dashboard'
import { Package } from '../../models/Package'
import { RecordFactory } from '../../models/RecordFactory'
import { getViewTitleFromView } from '../../utils/dashboard'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'
import { ServiceListTableCell } from './ServiceListTableCell'

type Props = {
    packages: Package[]
    dashboard?: Dashboard
    view: number
    searchText: string
    isLoading: boolean
}

const getKeyMetrics = (dashboard?: Dashboard, view?: number) => {
    const title = getViewTitleFromView(dashboard, view)

    if (view === 5) {
        return [
            { title: 'Unpaid packages', value: dashboard?.count?.toString() || '0' },
            { title: 'Amount to pay', value: currencyFormat(dashboard?.value ?? 0) },
        ]
    }

    return [
        {
            title: `${title} ${dashboard?.count === 1 ? `package` : `packages`}`,
            value: dashboard?.count?.toString() || '0',
        },
        {
            title: dashboard?.value === 1 ? 'Session' : 'Sessions',
            value: dashboard?.value?.toString() || '0',
        },
    ]
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.packages.map((p) => RecordFactory.getInstance().loadPackage(p.id!))
    )
}

function render({
    base: {
        props: { packages, dashboard, view, searchText, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric metrics={getKeyMetrics(dashboard, view)} />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Client</TableHead>
                        <TableHead className="font-normal">Package</TableHead>
                        <TableHead className="font-normal">Sessions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {packages.map((p) => (
                        <ClickableTableRow key={p.id} href={`/packages/${p.id}`}>
                            <TableCell>
                                <ClientTableCell user={p.client} subaccount={p.subaccount} />
                            </TableCell>

                            <TableCell>
                                <ServiceListTableCell services={p.lines} icon="package2.small" />
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm">
                                    {dashboard?.metaData[p.id ?? ''] ?? p.capacity} left
                                </Paragraph>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {packages.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const PackagesDashboard = usePSComponent(render, viewWillAppear)
